import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Loader2, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { authApi } from '@/api/auth';
import { useAuthStore } from '@/store/auth';
import AuthLayout from '@/components/layouts/AuthLayout';

const SetPasswordPage = () => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Das Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }
    if (password !== confirm) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }

    setLoading(true);
    try {
      await authApi.setInitialPassword(password);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern des Passworts.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Passwort festlegen"
      subtitle="Bitte legen Sie Ihr persönliches Passwort fest"
      showBackButton={false}
    >
      <div className="mb-6 p-4 bg-primary-50 border border-primary-200 rounded-lg flex gap-3">
        <Shield className="h-5 w-5 text-primary-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-primary-800">
          <strong>Sicherheitshinweis:</strong> Im Zuge unserer Infrastruktur-Aktualisierung bitten
          wir Sie, ein neues persönliches Passwort zu vergeben.
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="label">Neues Passwort</label>
          <div className="relative mt-1">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="input pr-10"
              placeholder="Mindestens 8 Zeichen"
              autoComplete="new-password"
              required
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff className="h-4 w-4 text-slate-400" /> : <Eye className="h-4 w-4 text-slate-400" />}
            </button>
          </div>
        </div>

        <div>
          <label className="label">Passwort bestätigen</label>
          <input
            type={showPassword ? 'text' : 'password'}
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            className="input mt-1"
            placeholder="Passwort wiederholen"
            autoComplete="new-password"
            required
          />
          {confirm && password === confirm && (
            <p className="mt-1 text-sm text-green-600 flex items-center gap-1">
              <CheckCircle className="h-3 w-3" /> Passwörter stimmen überein
            </p>
          )}
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">{error}</p>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-primary-900 hover:bg-primary-950 text-white py-3 font-medium border border-primary-800 flex items-center justify-center"
        >
          {loading ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Wird gespeichert...</>
          ) : (
            'Passwort speichern'
          )}
        </button>
      </form>
    </AuthLayout>
  );
};

export default SetPasswordPage;
